import React, { useState } from 'react';
import { Users, Mail, Shield, Plus, CheckCircle2, AlertCircle, BarChart3 } from 'lucide-react';

export default function TeamHub({ team, tasks }) {
  const [selectedDept, setSelectedDept] = useState('All');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Frontend Engineer');

  const departments = ['All', 'Engineering', 'AI & ML', 'Design', 'Product'];

  const filteredTeam = team.filter(m => selectedDept === 'All' || m.dept === selectedDept);

  const handleInvite = (e) => {
    e.preventDefault();
    alert(`Invitation successfully dispatched to ${inviteEmail} for role ${inviteRole}!`);
    setShowInviteModal(false);
    setInviteEmail('');
  };

  return (
    <div className="p-8 space-y-8 animate-fadeIn">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-900/60 rounded-full text-xs font-medium text-indigo-300">
            <Users className="w-3.5 h-3.5 text-indigo-400" /> Resource Allocation & Capacity
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Team Hub & Capacity Planning</h1>
          <p className="text-slate-300 text-sm max-w-xl">
            Monitor workloads across engineering, design, and AI divisions to prevent burnout and maximize sprint velocity.
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-5 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold text-sm transition shadow-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Invite Member
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-2">
          {departments.map(dept => (
            <button
              key={dept}
              onClick={() => setSelectedDept(dept)}
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition ${
                selectedDept === dept
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {dept}
            </button>
          ))}
        </div>

        <div className="text-xs text-slate-500 font-medium">
          Showing <strong className="text-slate-900">{filteredTeam.length}</strong> team members
        </div>
      </div>

      {/* Team Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTeam.map(member => {
          const memberTasks = tasks.filter(t => t.assigneeId === member.id && t.status !== 'done');
          const isOverloaded = member.workload > 85;

          return (
            <div key={member.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <img src={member.avatar} alt={member.name} className="w-12 h-12 rounded-2xl object-cover ring-2 ring-indigo-500/20" />
                    <div>
                      <h3 className="text-base font-bold text-slate-900">{member.name}</h3>
                      <p className="text-xs font-medium text-indigo-600">{member.role}</p>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${isOverloaded ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {isOverloaded ? 'High Load' : 'Optimal'}
                  </span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center text-slate-600 gap-2">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    <span>{member.email}</span>
                  </div>
                  <div className="flex items-center text-slate-600 gap-2">
                    <Shield className="w-3.5 h-3.5 text-slate-400" />
                    <span>Department: <strong className="text-slate-900">{member.dept}</strong></span>
                  </div>
                </div>

                {/* Workload Meter */}
                <div className="space-y-1.5 pt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-500">Workload Capacity</span>
                    <span className={`font-bold ${isOverloaded ? 'text-rose-600' : 'text-slate-900'}`}>{member.workload}% ({member.capacity} hrs/wk)</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${isOverloaded ? 'bg-rose-500' : 'bg-indigo-600'}`} 
                      style={{ width: `${member.workload}%` }}
                    ></div>
                  </div>
                </div>

                {/* Active Tasks list */}
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">Active Tasks ({memberTasks.length})</p>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {memberTasks.length === 0 && (
                      <p className="text-xs text-slate-400 italic">No pending tasks assigned.</p>
                    )}
                    {memberTasks.map(t => (
                      <div key={t.id} className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs flex items-center justify-between">
                        <span className="font-medium text-slate-800 truncate max-w-[180px]">{t.title}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${t.priority === 'Urgent' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>
                          {t.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>Capacity Status</span>
                <span className="font-semibold text-slate-900">Available</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-6">
            <h2 className="text-lg font-bold text-slate-900">Invite Team Member</h2>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Email Address</label>
                <input 
                  type="email"
                  required
                  placeholder="colleague@nexusflow.io"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Role / Title</label>
                <select 
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-medium"
                >
                  <option value="Frontend Engineer">Frontend Engineer</option>
                  <option value="Backend Developer">Backend Developer</option>
                  <option value="AI Research Scientist">AI Research Scientist</option>
                  <option value="Product Manager">Product Manager</option>
                  <option value="UX Designer">UX Designer</option>
                </select>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end space-x-3">
                <button 
                  type="button" 
                  onClick={() => setShowInviteModal(false)}
                  className="px-5 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 shadow-md shadow-indigo-600/20"
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
